import React, { useEffect, useState } from 'react';
import TrashIcon from '../../assets/images/icons/TrashIcon.svg';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Alert, Box, IconButton, Stack, Typography } from '@mui/material';

import DeleteConfirmModal from '../modals/DeleteConfirmModal.js';
import { isAdmin } from '../../helpers/helpers_general.js';
import {
  deleteDocument,
  getDocumentsByContent,
  saveDocument,
} from '../../services/documents.js';
import { uploadFileS3 } from '../../services/file';
import Loading from '../loading/loading';
import FileButton from '../Input/fileButton';

const BookFiles = ({ contentId }) => {
  const initialState = {
    showModal: false,
    loading: true,
    error: false,
    bookFiles: [],
    loadingBookFiles: true,
    loadingFile: false,
    fileName: '',
    errorMessage: '',
  };

  const [bookStates, setBookStates] = useState(initialState);

  useEffect(() => {
    getDocumentsByContent(contentId)
      .then(response =>
        setBookStates(prevState => ({
          ...prevState,
          bookFiles: response.data,
          loadingBookFiles: true,
        }))
      )
      .finally(() =>
        setBookStates(prevState => ({ ...prevState, loadingBookFiles: false }))
      );
  }, [contentId]);

  const deleteDocumentFile = id => {
    deleteDocument(id).then(
      response => {
        getDocumentsByContent(contentId).then(response =>
          setBookStates(prevState => ({
            ...prevState,
            bookFiles: response.data,
            error: false,
          }))
        );
      },
      error => {
        getDocumentsByContent(contentId).then(response =>
          setBookStates(prevState => ({
            ...prevState,
            bookFiles: response.data,
            error: true,
            errorMessage: 'Um erro ocorreu ao deletar o arquivo.',
          }))
        );
      }
    );
    setBookStates(prevState => ({ ...prevState, showModal: false }));
  };

  const uploadFile = e => {
    setBookStates(prevState => ({ ...prevState, loadingFile: true }));
    uploadFileS3(e, bookStates.documentName).then(data => {
      if (data !== undefined) {
        setBookStates(prevState => ({
          ...prevState,
          loadingFile: true,
          fileName: data.key,
        }));

        const fileExtension = data.key.includes('.') 
          ? data.key.split('.').pop() 
          : 'unknown';
        
        saveDocument({
          content_id: contentId,
          file_locale: data.location,
          file_file_name: data.key,
          file_content_type: fileExtension,
        }).then(
          r => {
            getDocumentsByContent(contentId).then(response =>
              setBookStates(prevState => ({ ...prevState, bookFiles: response.data }))
            );
            setBookStates(prevState => ({
              ...prevState,
              loadingFile: false,
              fileName: '',
              error: false,
            }));
          },
          e => {
            setBookStates(prevState => ({
              ...prevState,
              loadingFile: false,
              fileName: '',
              errorMessage: 'Um erro ocorreu ao deletar o arquivo.',
              error: true,
            }));
          }
        );
      } else {
        setBookStates(prevState => ({
          ...prevState,
          loadingFile: false,
          fileName: '',
        }));
      }
    });
  };

  const getExt = name => {
    if (!name || !name.includes('.')) return 'FILE';
    return name.split('.').pop().toUpperCase();
  };

  const extColor = ext => {
    const map = { PDF: '#e53935', DOC: '#1565c0', DOCX: '#1565c0', XLS: '#2e7d32', XLSX: '#2e7d32', PPT: '#e65100', PPTX: '#e65100' };
    return map[ext] || '#006494';
  };

  return (
    <Box>
      <DeleteConfirmModal
        showModal={bookStates.showModal}
        setStates={setBookStates}
        deleteCard={() => deleteDocumentFile(bookStates.documentId)}
        title={bookStates.documentName}
      />

      {/* Título da seção */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '20px', mb: '12px' }}>
        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Arquivos
        </Typography>
        {!bookStates.loadingBookFiles && bookStates.bookFiles.length > 0 && (
          <Box sx={{ backgroundColor: '#006494', color: '#fff', fontSize: '10px', fontWeight: 700, borderRadius: '10px', padding: '1px 7px' }}>
            {bookStates.bookFiles.length}
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {bookStates.loadingBookFiles ? (
          <Box sx={{ width: '50px', my: '12px' }}>
            <Loading />
          </Box>
        ) : bookStates.bookFiles.length === 0 ? (
          <Typography sx={{ fontSize: '13px', color: '#9e9e9e' }}>
            Nenhum arquivo disponível para download.
          </Typography>
        ) : (
          bookStates.bookFiles.map((doc, index) => {
            const ext = getExt(doc.file_file_name);
            return (
              <Box
                key={`${doc.id}-${index}`}
                sx={{
                  display: 'flex', alignItems: 'center',
                  border: '1px solid #e8eaed', borderRadius: '8px',
                  backgroundColor: '#fff', overflow: 'hidden',
                  transition: 'box-shadow 0.15s',
                  '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderColor: '#c5d8e8' },
                }}
              >
                {/* Badge de extensão */}
                <Box sx={{
                  flexShrink: 0, width: '44px', alignSelf: 'stretch',
                  backgroundColor: extColor(ext),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Typography sx={{ fontSize: '9px', fontWeight: 800, color: '#fff', letterSpacing: '0.05em' }}>
                    {ext}
                  </Typography>
                </Box>

                {/* Nome do arquivo */}
                <Typography sx={{
                  flex: 1, fontSize: '13px', color: '#3E3D3D', fontWeight: 500,
                  px: '12px', py: '10px',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {doc.file_file_name}
                </Typography>

                {/* Ações */}
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, pr: '4px' }}>
                  <IconButton
                    component="a"
                    href={doc.file_locale}
                    target="_blank"
                    rel="noreferrer"
                    size="small"
                    sx={{ color: '#006494' }}
                  >
                    <FileDownloadOutlinedIcon fontSize="small" />
                  </IconButton>
                  {isAdmin() && (
                    <IconButton
                      size="small"
                      sx={{ color: '#e53935' }}
                      onClick={() => setBookStates(prev => ({ ...prev, showModal: true, documentName: doc.file_file_name, documentId: doc.id }))}
                    >
                      <img alt="deletar" src={TrashIcon} style={{ width: '16px', height: '16px' }} />
                    </IconButton>
                  )}
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {isAdmin() && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: '16px' }}>
          {bookStates.error && (
            <Stack sx={{ width: '35%', mr: '12px' }}>
              <Alert severity="error">{bookStates.errorMessage}</Alert>
            </Stack>
          )}
          <FileButton onChange={e => uploadFile(e)} />
        </Box>
      )}
    </Box>
  );
};

export default BookFiles;
